import {useCallback, useMemo} from 'react';
import {addDoc, collection, deleteDoc, deleteField, doc, getDoc, query, updateDoc, where} from 'firebase/firestore';
import {useCollection} from 'react-firebase-hooks/firestore';
import {Reminder, ReminderInsertOptions, ReminderUpdateOptions} from '../types/Reminder'; // Assuming this path
import {MONGO_DATE_TIME_FORMAT} from "../utils/date";
import moment from "moment";
import {firestore} from "../App";
import {processRemindAt, processSteps} from "./common/event";

interface UseReminderManagerProps {
    plannerId: string;
}

export function useReminderManager({plannerId}: UseReminderManagerProps) {

    const remindersQuery = useMemo(() => {
        if (!plannerId) return null;

        return query(
            collection(firestore, 'syncedPlanners', plannerId, 'events'),
            where("eventKind", "==", "reminder") // Filter for 'reminder'
        );
    }, [plannerId]);

    const [remindersSnapshot, loadingReminders, errorReminders] = useCollection(remindersQuery);

    const reminders = useMemo(() => {
        return remindersSnapshot?.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
        } as Reminder)) || []; // Cast as Reminder
    }, [remindersSnapshot]);

    /**
     * Inserts a reminder document in the database.
     */
    const createReminder = useCallback(async (options: ReminderInsertOptions) => {
        if (!plannerId) return;

        const reminderDoc: any = {
            eventKind: 'reminder', // Set eventKind
            plannerId: plannerId,
            title: options.title,
            date: options.date,
            isArchived: options.isArchived,
            note: options.note,
            color: options.color,
            completedOn: undefined,
            ...processRemindAt(options.remindAt),
            ...processSteps(options.steps),
            createdOn: moment().format(MONGO_DATE_TIME_FORMAT)
        };

        // Firestore does not allow 'undefined' values.
        Object.keys(reminderDoc).forEach(key => {
            if (reminderDoc[key] === undefined) {
                delete reminderDoc[key];
            }
        });

        const colRef = collection(firestore, 'syncedPlanners', plannerId, 'events');

        try {
            console.log("Creating Reminder: ", reminderDoc);
            const docRef = await addDoc(colRef, reminderDoc);
            console.log("Reminder created successfully with ID: ", docRef.id);
            return docRef.id;
        } catch (error) {
            console.error("Error creating Reminder: ", error);
        }
    }, [plannerId]);

    /**
     * Updates a reminder document in the database.
     */
    const updateReminder = useCallback(async (reminderId: string, options: ReminderUpdateOptions) => {
        if (!plannerId) return;

        const docRef = doc(firestore, 'syncedPlanners', plannerId, 'events', reminderId);

        const updateData: { [key: string]: any } = {
            title: options.title,
            date: options.date,
            isArchived: options.isArchived,
            note: options.note ?? deleteField(),
            completedOn: options.completedOn ?? deleteField(),
            ...processRemindAt(options.remindAt),
            ...processSteps(options.steps)
        };

        if (options.color) {
            updateData["color"] = options.color;
        }

        try {
            console.log("Updating Reminder: ", updateData);
            await updateDoc(docRef, updateData);
            console.log("Reminder created updated with ID: ", docRef.id);
            return docRef.id;
        } catch (error) {
            console.error("Error updating Reminder: ", error);
        }
    }, [plannerId]);

    /**
     * Toggles the completed_on property of a reminder.
     */
    const toggleReminderCompletedOn = useCallback(async (reminderId: string) => {
        if (!plannerId) return;

        const docRef = doc(firestore, 'syncedPlanners', plannerId, 'events', reminderId);

        try {
            const docSnap = await getDoc(docRef);
            if (!docSnap.exists()) {
                console.error("Reminder document not found");
                return;
            }

            const currentReminder = docSnap.data() as Reminder;
            const newCompletedOn = currentReminder.completedOn
                ? deleteField()
                : moment().format(MONGO_DATE_TIME_FORMAT);

            await updateDoc(docRef, {completedOn: newCompletedOn});

        } catch (error) {
            console.error("Error toggling Reminder: ", error);
        }
    }, [plannerId]);

    /**
     * Deletes a reminder from the database.
     */
    const deleteReminder = useCallback(async (reminderId: string) => {
        if (!plannerId) return;

        const docRef = doc(firestore, 'syncedPlanners', plannerId, 'events', reminderId);
        await deleteDoc(docRef);
    }, [plannerId]);

    return {
        reminders,
        loadingReminders,
        errorReminders,
        createReminder,
        updateReminder,
        toggleReminderCompletedOn,
        deleteReminder
    };
}